import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import AgentLayout from '../../components/agent/AgentLayout'
import { useAuth } from '../../context/AuthContext'
import { API_BASE } from '../../utils/api'

interface ClassifiedForm {
    propertyCategory: string;
    subCategory: string;
    propertyType: string;
    price: string;
    priceMin: string;
    priceMax: string;
    size: string;
    buildingName: string;
    area: string;
    city: string;
    nearbyAddress: string;
    occupancyType: string;
    propertyAge: string;
    floor: string;
    washrooms: string;
    bhkConfiguration: string;
    bhkConfigurations: string[];
    furnishingType: string;
    description: string;
    noOfBlocks: string;
    possessionDate: string;
    launchDate: string;
    reraId: string;
    totalUnit: string;
    facing: string;
    typeOfParking: string;
    noOfParking: string;
    basicRate: string;
    dpRate: string;
}

const subCategoryMapping: Record<string, string[]> = {
    'RESIDENTIAL': [
        'Flat/Apartment', 'Villa', 'Bungalow', 'Row House', 'Studio Apartment', 'Farm House', 'Residential Plot', 'Serviced Apartment'
    ],
    'COMMERCIAL': [
        'Office Space', 'Showroom', 'Shop', 'Office Space, Showroom', 'Co-working Space', 'Warehouse', 'Industrial Shed', 'Commercial Plot'
    ],
    'PG / CO-LIVING': [
        'PG Accommodation', 'Co-living Space'
    ],
    'PLOT': [
        'Residential Plot', 'Villa Plot', 'Weekend Plot'
    ],
    'LAND': [
        'NA Land', 'Agricultural Land', 'Industrial Land', 'Commercial Land'
    ],
    'BANK AUCTION PROPERTY': [
        'Flat/Apartment', 'Office Space', 'Shop', 'Residential Plot', 'Bungalow'
    ]
};

export default function AgentAddProperty() {
    const { token } = useAuth()
    const navigate = useNavigate()
    const [step, setStep] = useState(1)
    const [submitting, setSubmitting] = useState(false)
    const [msg, setMsg] = useState<{ type: 'success' | 'error', text: string } | null>(null)

    // Step 1: Basics specific state
    const [actionType, setActionType] = useState('list_property') // list_property, post_requirement, builder_project
    const [propertyTypeSelection, setPropertyTypeSelection] = useState('Residential Rent')

    // Amenities specific state
    const [amenities, setAmenities] = useState<string[]>([])
    const [occupancyOpen, setOccupancyOpen] = useState(false)

    // Step 2, 3, 4 state
    const [form, setForm] = useState<ClassifiedForm>({
        propertyCategory: 'RESIDENTIAL',
        subCategory: 'Flat/Apartment',
        propertyType: 'Residential',
        price: '',
        priceMin: '',
        priceMax: '',
        size: '',
        buildingName: '',
        area: '',
        city: '',
        nearbyAddress: '',
        occupancyType: 'Only For Family',
        propertyAge: '',
        floor: '',
        washrooms: '1',
        bhkConfiguration: '',
        bhkConfigurations: [],
        furnishingType: '',
        description: '',
        noOfBlocks: '',
        possessionDate: '',
        launchDate: '',
        reraId: '',
        totalUnit: '',
        facing: '',
        typeOfParking: '',
        noOfParking: '',
        basicRate: '',
        dpRate: ''
    })

    const fillDummyData = () => {
        const dummyPresets = [
            {
                action: 'builder_project',
                typeSelection: 'Residential',
                category: 'RESIDENTIAL',
                subCategory: 'Flat/Apartment',
                formValues: {
                    propertyCategory: 'RESIDENTIAL',
                    subCategory: 'Flat/Apartment',
                    propertyType: 'Residential',
                    price: '',
                    priceMin: '4500000',
                    priceMax: '12000000',
                    size: '1250',
                    buildingName: 'Gulmohar Greens',
                    area: 'Prahlad Nagar',
                    city: 'Ahmedabad',
                    nearbyAddress: 'Opp. Prahladnagar Garden',
                    occupancyType: 'Only For Family',
                    propertyAge: 'New Construction',
                    floor: '7',
                    washrooms: '3',
                    bhkConfiguration: '3 BHK',
                    bhkConfigurations: ['2 BHK', '3 BHK'],
                    furnishingType: 'Semi-Furnished',
                    description: 'Modern luxurious builder project located in the heart of Ahmedabad with premium amenities.',
                    noOfBlocks: '4',
                    possessionDate: '2027-12-31',
                    launchDate: '2025-01-01',
                    reraId: 'PR/GJ/AHM/1029302',
                    totalUnit: '128',
                    facing: 'East',
                    typeOfParking: 'Covered',
                    noOfParking: '2',
                    basicRate: '4500',
                    dpRate: '4100'
                },
                amenitiesList: ['Swimming Pool', 'Gymnasium', 'Club House', 'CCTV']
            },
            {
                action: 'list_property',
                typeSelection: 'Residential Sell',
                category: 'RESIDENTIAL',
                subCategory: 'Villa',
                formValues: {
                    propertyCategory: 'RESIDENTIAL',
                    subCategory: 'Villa',
                    propertyType: 'Residential',
                    price: '25000000',
                    priceMin: '',
                    priceMax: '',
                    size: '3500',
                    buildingName: 'Prestige Villas',
                    area: 'Whitefield',
                    city: 'Bangalore',
                    nearbyAddress: 'Near Prestige Tech Park',
                    occupancyType: 'Family-Bachelors',
                    propertyAge: '1-5 Yrs',
                    floor: '2',
                    washrooms: '4',
                    bhkConfiguration: '4 BHK',
                    bhkConfigurations: [],
                    furnishingType: 'Fully Furnished',
                    description: 'Stunning ready-to-move villa in Whitefield Bangalore with ultra luxury interior.',
                    noOfBlocks: '',
                    possessionDate: '',
                    launchDate: '',
                    reraId: '',
                    totalUnit: '',
                    facing: 'North-East',
                    typeOfParking: 'Open',
                    noOfParking: '3',
                    basicRate: '',
                    dpRate: ''
                },
                amenitiesList: ['Garden', 'Security', 'Intercom']
            },
            {
                action: 'list_property',
                typeSelection: 'Commercial Rent',
                category: 'COMMERCIAL',
                subCategory: 'Office Space',
                formValues: {
                    propertyCategory: 'COMMERCIAL',
                    subCategory: 'Office Space',
                    propertyType: 'Commercial',
                    price: '150000',
                    priceMin: '',
                    priceMax: '',
                    size: '1800',
                    buildingName: 'Sigma Trade Center',
                    area: 'Bandra Kurla Complex',
                    city: 'Mumbai',
                    nearbyAddress: 'Opp. NSE Building',
                    occupancyType: '',
                    propertyAge: 'Under Construction',
                    floor: '12',
                    washrooms: '2',
                    bhkConfiguration: '',
                    bhkConfigurations: [],
                    furnishingType: 'Kitchen-Fix',
                    description: 'Premium corporate office space at BKC Mumbai with top-tier business amenities.',
                    noOfBlocks: '',
                    possessionDate: '',
                    launchDate: '',
                    reraId: '',
                    totalUnit: '',
                    facing: 'West',
                    typeOfParking: 'Covered',
                    noOfParking: '4',
                    basicRate: '',
                    dpRate: ''
                },
                amenitiesList: ['Power Backup', 'Lift', 'Wi-Fi']
            }
        ];

        const randomPreset = dummyPresets[Math.floor(Math.random() * dummyPresets.length)]

        setActionType(randomPreset.action)
        setPropertyTypeSelection(randomPreset.typeSelection)
        setForm(randomPreset.formValues)
        setAmenities(randomPreset.amenitiesList)
        setMsg({ type: 'success', text: `Dummy data populated for ${randomPreset.formValues.buildingName}!` })
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        setForm(p => ({ ...p, [e.target.name]: e.target.value }))
    }

    const nextStep = () => {
        if (step === 1) {
            setStep(2)
        } else if (step === 2) {
            if (isBuilderResidential) {
                if (!form.priceMin || !form.priceMax || !form.area || !form.city || !form.description) {
                    setMsg({ type: 'error', text: 'Please fill in all required fields marked with *' })
                    return
                }
            } else {
                if (!form.price || !form.area || !form.city || !form.description) {
                    setMsg({ type: 'error', text: 'Please fill in all required fields marked with *' })
                    return
                }
            }
            setMsg(null)
            if (actionType === 'builder_project') {
                setStep(3)
            } else {
                setStep(4)
            }
        } else if (step === 3) {
            setStep(4)
        }
    }

    const prevStep = () => {
        if (step === 2) {
            setStep(1)
        } else if (step === 3) {
            setStep(2)
        } else if (step === 4) {
            if (actionType === 'builder_project') {
                setStep(3)
            } else {
                setStep(2)
            }
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!token) {
            setMsg({ type: 'error', text: 'Please login first.' })
            return
        }
        setSubmitting(true)
        setMsg(null)

        try {
            const res = await fetch(`${API_BASE}/classified-properties`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    ...form,
                    propertyType: propertyTypeSelection || form.propertyType,
                    price: Number(form.price),
                    priceMin: Number(form.priceMin || 0),
                    priceMax: Number(form.priceMax || 0),
                    size: Number(form.size || 0),
                    washrooms: Number(form.washrooms || 1),
                    amenities
                })
            })
            const data = await res.json()
            if (res.ok) {
                setMsg({ type: 'success', text: 'Property listed successfully!' })
                setTimeout(() => navigate('/agent-my-property'), 1500)
            } else {
                setMsg({ type: 'error', text: data.message || 'Failed to list property.' })
            }
        } catch (err) {
            setMsg({ type: 'error', text: 'Server error. Please try again.' })
        }
        setSubmitting(false)
    }

    const bhkOptions = ['Studio Apartment', '1 BHK', '2 BHK', '3 BHK', '4 BHK', '5 BHK', '6 BHK', '6+ BHK']
    const furnishingOptions = ['Fully Furnished', 'Furnished', 'Semi-Furnished', 'Kitchen-Fix', 'Unfurnished']
    const cityOptions = ['Ahmedabad', 'Mumbai', 'Delhi', 'Bangalore', 'Hyderabad', 'Pune', 'Chennai', 'Kolkata']
    const facingOptions = ['East', 'West', 'North', 'South', 'North-East', 'North-West', 'South-East', 'South-West']
    const parkingOptions = ['Covered', 'Open', 'None']
    const commonAmenities = ['Swimming Pool', 'Gym', 'Club House', 'Parking', '24x7 Security', 'Lift', 'Power Backup', 'Play Area']

    const toggleAmenity = (am: string) => {
        setAmenities(prev => prev.includes(am) ? prev.filter(x => x !== am) : [...prev, am])
    }

    const toggleBhkConfiguration = (bhk: string) => {
        setForm(p => {
            const current = p.bhkConfigurations || []
            const updated = current.includes(bhk) ? current.filter(x => x !== bhk) : [...current, bhk]
            return { ...p, bhkConfigurations: updated }
        })
    }

    const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const cat = e.target.value
        const firstSub = subCategoryMapping[cat]?.[0] || ''
        setForm(p => ({ ...p, propertyCategory: cat, subCategory: firstSub }))
    }

    const isBuilder = actionType === 'builder_project'
    const isBuilderResidential = actionType === 'builder_project' && propertyTypeSelection === 'Residential'

    return (
        <AgentLayout>
            {/* Custom Dynamic Stepper */}
            <div className="max-w-4xl mx-auto mt-6 mb-12 flex items-center justify-between select-none">
                {/* 1. Basics */}
                <div className="flex flex-col items-center">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center border-2 transition-all duration-200 ${step === 1 ? 'bg-[#5B32B4] border-[#5B32B4] text-white shadow-lg' : 'bg-emerald-500 border-emerald-500 text-white'}`}>
                        {step === 1 ? <i className="fa-solid fa-house text-sm"></i> : <i className="fa-solid fa-check text-sm"></i>}
                    </div>
                    <span className={`text-xs font-bold mt-2 ${step === 1 ? 'text-[#5B32B4]' : 'text-emerald-600'}`}>Basics</span>
                </div>
                <div className={`h-0.5 flex-1 mx-4 self-center -mt-6 transition-all duration-200 ${step >= 2 ? 'bg-emerald-500' : 'bg-slate-200'}`}></div>

                {/* 2. Details */}
                <div className="flex flex-col items-center">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center border-2 transition-all duration-200 ${step === 2 ? 'bg-[#5B32B4] border-[#5B32B4] text-white shadow-lg' : step > 2 ? 'bg-emerald-500 border-emerald-500 text-white' : 'bg-slate-100 border-slate-200 text-slate-400'}`}>
                        <i className="fa-solid fa-location-dot text-sm"></i>
                    </div>
                    <span className={`text-xs font-bold mt-2 ${step === 2 ? 'text-[#5B32B4]' : step > 2 ? 'text-emerald-600' : 'text-slate-400'}`}>Details</span>
                </div>
                <div className={`h-0.5 flex-1 mx-4 self-center -mt-6 transition-all duration-200 ${step >= 3 ? 'bg-emerald-500' : 'bg-slate-200'}`}></div>

                {/* Optional Step 3. Amenities */}
                {isBuilder && (
                    <>
                        <div className="flex flex-col items-center">
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center border-2 transition-all duration-200 ${step === 3 ? 'bg-[#5B32B4] border-[#5B32B4] text-white shadow-lg' : step > 3 ? 'bg-emerald-500 border-emerald-500 text-white' : 'bg-slate-100 border-slate-200 text-slate-400'}`}>
                                <i className="fa-solid fa-circle-check text-sm"></i>
                            </div>
                            <span className={`text-xs font-bold mt-2 ${step === 3 ? 'text-[#5B32B4]' : step > 3 ? 'text-emerald-600' : 'text-slate-400'}`}>Amenities</span>
                        </div>
                        <div className={`h-0.5 flex-1 mx-4 self-center -mt-6 transition-all duration-200 ${step >= 4 ? 'bg-emerald-500' : 'bg-slate-200'}`}></div>
                    </>
                )}

                {/* Step 4 (or 3 for non-builder). Contact & Media */}
                <div className="flex flex-col items-center">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center border-2 transition-all duration-200 ${step === 4 ? 'bg-[#5B32B4] border-[#5B32B4] text-white shadow-lg' : 'bg-slate-100 border-slate-200 text-slate-400'}`}>
                        <i className="fa-regular fa-user text-sm"></i>
                    </div>
                    <span className={`text-xs font-bold mt-2 ${step === 4 ? 'text-[#5B32B4]' : 'text-slate-400'}`}>Contact & Media</span>
                </div>
            </div>

            {msg && (
                <div className={`p-4 rounded-xl mb-6 font-bold text-sm max-w-4xl mx-auto ${msg.type === 'success' ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' : 'bg-rose-50 text-rose-600 border border-rose-100'}`}>
                    {msg.text}
                </div>
            )}

            <div className="bg-white rounded-2xl p-6 md:p-10 max-w-4xl mx-auto mb-12 select-none">
                {step === 1 && (
                    <div className="space-y-10">
                        {/* 1. What would you like to do? */}
                        <div>
                            <div className="flex justify-between items-center mb-6">
                                <h2 className="text-xl font-bold text-[#0f172a]">What would you like to do?</h2>
                                <button
                                    type="button"
                                    onClick={fillDummyData}
                                    className="px-4 py-2 bg-emerald-50 text-emerald-600 hover:bg-emerald-100 rounded-xl font-bold text-xs transition-all flex items-center gap-1 shadow-sm select-none"
                                >
                                    <i className="fa-solid fa-wand-magic-sparkles"></i> Fill Dummy Data
                                </button>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {/* List Property */}
                                <div
                                    onClick={() => { setActionType('list_property'); setPropertyTypeSelection('Residential Rent') }}
                                    className={`p-6 rounded-2xl border-2 cursor-pointer transition-all flex flex-col justify-between h-[150px] relative ${actionType === 'list_property' ? 'bg-[#3b82f6] border-[#3b82f6] text-white shadow-lg' : 'bg-white border-slate-200 text-slate-800 hover:bg-slate-50'}`}
                                >
                                    <div className="text-2xl mb-2">
                                        <i className="fa-solid fa-house"></i>
                                    </div>
                                    <div>
                                        <h3 className={`font-bold text-base leading-none ${actionType === 'list_property' ? 'text-white' : 'text-slate-800'}`}>List Property</h3>
                                        <span className={`text-xs font-medium mt-1 inline-block ${actionType === 'list_property' ? 'text-blue-100' : 'text-slate-500'}`}>Sell or rent</span>
                                    </div>
                                </div>

                                {/* Post Requirement */}
                                <div
                                    onClick={() => { setActionType('post_requirement'); setPropertyTypeSelection('Residential Rent') }}
                                    className={`p-6 rounded-2xl border-2 cursor-pointer transition-all flex flex-col justify-between h-[150px] relative ${actionType === 'post_requirement' ? 'bg-[#10b981] border-[#10b981] text-white shadow-lg' : 'bg-white border-slate-200 text-slate-800 hover:bg-slate-50'}`}
                                >
                                    <div className="text-2xl mb-2">
                                        <i className="fa-solid fa-magnifying-glass"></i>
                                    </div>
                                    <div>
                                        <h3 className={`font-bold text-base leading-none ${actionType === 'post_requirement' ? 'text-white' : 'text-slate-800'}`}>Post Requirement</h3>
                                        <span className={`text-xs font-medium mt-1 inline-block ${actionType === 'post_requirement' ? 'text-emerald-100' : 'text-slate-500'}`}>Find properties</span>
                                    </div>
                                </div>

                                {/* Builder Project */}
                                <div
                                    onClick={() => { setActionType('builder_project'); setPropertyTypeSelection('Residential') }}
                                    className={`p-6 rounded-2xl border-2 cursor-pointer transition-all flex flex-col justify-between h-[150px] relative ${actionType === 'builder_project' ? 'bg-[#8b5cf6] border-[#8b5cf6] text-white shadow-lg' : 'bg-white border-slate-200 text-slate-800 hover:bg-slate-50'}`}
                                >
                                    <div className="text-2xl mb-2">
                                        <i className="fa-solid fa-building"></i>
                                    </div>
                                    <div>
                                        <h3 className={`font-bold text-base leading-none ${actionType === 'builder_project' ? 'text-white' : 'text-slate-800'}`}>Builder Project</h3>
                                        <span className={`text-xs font-medium mt-1 inline-block ${actionType === 'builder_project' ? 'text-purple-100' : 'text-slate-500'}`}>New projects</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* 2. Select type */}
                        <div>
                            <h2 className="text-xl font-bold text-[#0f172a] mb-6">Select type</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {!isBuilder ? (
                                    <>
                                        <div
                                            onClick={() => setPropertyTypeSelection('Residential Rent')}
                                            className={`p-5 rounded-2xl border cursor-pointer transition-all flex items-center justify-center gap-3 h-[70px] ${propertyTypeSelection === 'Residential Rent' ? 'bg-[#2563eb] border-[#2563eb] text-white shadow-md' : 'bg-white border-slate-200 text-slate-800 hover:bg-slate-50'}`}
                                        >
                                            <i className="fa-solid fa-house text-lg"></i>
                                            <span className="font-bold text-sm">Residential Rent</span>
                                        </div>

                                        <div
                                            onClick={() => setPropertyTypeSelection('Residential Sell')}
                                            className={`p-5 rounded-2xl border cursor-pointer transition-all flex items-center justify-center gap-3 h-[70px] ${propertyTypeSelection === 'Residential Sell' ? 'bg-[#2563eb] border-[#2563eb] text-white shadow-md' : 'bg-white border-slate-200 text-slate-800 hover:bg-slate-50'}`}
                                        >
                                            <i className="fa-solid fa-house text-lg"></i>
                                            <span className="font-bold text-sm">Residential Sell</span>
                                        </div>

                                        <div
                                            onClick={() => setPropertyTypeSelection('Commercial Rent')}
                                            className={`p-5 rounded-2xl border cursor-pointer transition-all flex items-center justify-center gap-3 h-[70px] ${propertyTypeSelection === 'Commercial Rent' ? 'bg-[#2563eb] border-[#2563eb] text-white shadow-md' : 'bg-white border-slate-200 text-slate-800 hover:bg-slate-50'}`}
                                        >
                                            <i className="fa-solid fa-building text-lg"></i>
                                            <span className="font-bold text-sm">Commercial Rent</span>
                                        </div>

                                        <div
                                            onClick={() => setPropertyTypeSelection('Commercial Sell')}
                                            className={`p-5 rounded-2xl border cursor-pointer transition-all flex items-center justify-center gap-3 h-[70px] ${propertyTypeSelection === 'Commercial Sell' ? 'bg-[#2563eb] border-[#2563eb] text-white shadow-md' : 'bg-white border-slate-200 text-slate-800 hover:bg-slate-50'}`}
                                        >
                                            <i className="fa-solid fa-building text-lg"></i>
                                            <span className="font-bold text-sm">Commercial Sell</span>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div
                                            onClick={() => setPropertyTypeSelection('Residential')}
                                            className={`p-5 rounded-2xl border cursor-pointer transition-all flex items-center justify-center gap-3 h-[70px] ${propertyTypeSelection === 'Residential' ? 'bg-[#2563eb] border-[#2563eb] text-white shadow-md' : 'bg-white border-slate-200 text-slate-800 hover:bg-slate-50'}`}
                                        >
                                            <i className="fa-solid fa-house text-lg"></i>
                                            <span className="font-bold text-sm">Residential</span>
                                        </div>

                                        <div
                                            onClick={() => setPropertyTypeSelection('Commercial')}
                                            className={`p-5 rounded-2xl border cursor-pointer transition-all flex items-center justify-center gap-3 h-[70px] ${propertyTypeSelection === 'Commercial' ? 'bg-[#2563eb] border-[#2563eb] text-white shadow-md' : 'bg-white border-slate-200 text-slate-800 hover:bg-slate-50'}`}
                                        >
                                            <i className="fa-solid fa-building text-lg"></i>
                                            <span className="font-bold text-sm">Commercial</span>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Next Footer Button */}
                        <div className="flex justify-end pt-8 border-t border-gray-100">
                            <button
                                type="button"
                                onClick={nextStep}
                                className="px-8 py-3 bg-[#5B32B4] hover:bg-slate-800 text-white rounded-xl font-bold text-sm transition-all shadow-xl hover:shadow-2xl active:scale-95 flex items-center justify-center gap-2 select-none"
                            >
                                Next <i className="fa-solid fa-chevron-right text-xs"></i>
                            </button>
                        </div>
                    </div>
                )}

                {step === 2 && !isBuilderResidential && (
                    <div className="space-y-8">
                        <h2 className="text-2xl font-bold text-[#0f172a] mb-8 leading-none">Property Details</h2>

                        <form onSubmit={nextStep} className="space-y-8">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-6">
                                {/* Property Category */}
                                <div>
                                    <label className="block text-xs font-bold text-slate-600 mb-2">Property Category *</label>
                                    <select className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium text-slate-700 focus:ring-2 focus:ring-[#5B32B4] focus:border-[#5B32B4] outline-none transition-all" name="propertyCategory" value={form.propertyCategory} onChange={handleCategoryChange} required>
                                        <option value="RESIDENTIAL">RESIDENTIAL</option>
                                        <option value="COMMERCIAL">COMMERCIAL</option>
                                        <option value="PG / CO-LIVING">PG / CO-LIVING</option>
                                        <option value="PLOT">PLOT</option>
                                        <option value="LAND">LAND</option>
                                        <option value="BANK AUCTION PROPERTY">BANK AUCTION PROPERTY</option>
                                    </select>
                                </div>

                                {/* Sub Category */}
                                <div>
                                    <label className="block text-xs font-bold text-slate-600 mb-2">Sub Category *</label>
                                    <select className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium text-slate-700 focus:ring-2 focus:ring-[#5B32B4] focus:border-[#5B32B4] outline-none transition-all" name="subCategory" value={form.subCategory} onChange={handleChange} required>
                                        {(subCategoryMapping[form.propertyCategory] || []).map(sc => (
                                            <option key={sc} value={sc}>{sc}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* Price */}
                                <div>
                                    <label className="block text-xs font-bold text-slate-600 mb-2">Price *</label>
                                    <div className="relative">
                                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-sm">₹</span>
                                        <input type="number" className="w-full bg-white border border-slate-200 rounded-xl pl-8 pr-4 py-3 text-sm font-medium text-slate-700 focus:ring-2 focus:ring-[#5B32B4] focus:border-[#5B32B4] outline-none transition-all" name="price" value={form.price} onChange={handleChange} placeholder="Enter price" required />
                                    </div>
                                </div>

                                {/* Size */}
                                <div>
                                    <label className="block text-xs font-bold text-slate-600 mb-2">Size</label>
                                    <div className="flex border border-slate-200 rounded-xl overflow-hidden bg-white focus-within:ring-2 focus-within:ring-[#5B32B4] focus-within:border-[#5B32B4] transition-all">
                                        <select className="bg-slate-50 border-r border-slate-200 px-3 py-3 text-sm font-medium text-slate-600 outline-none">
                                            <option>SQFT</option>
                                        </select>
                                        <input type="number" className="w-full px-4 py-3 text-sm font-medium text-slate-700 outline-none" name="size" value={form.size} onChange={handleChange} placeholder="Enter area in SQFT" />
                                    </div>
                                </div>

                                {/* Building Name */}
                                <div>
                                    <label className="block text-xs font-bold text-slate-600 mb-2">Building Name / Premise Name</label>
                                    <input type="text" className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium text-slate-700 focus:ring-2 focus:ring-[#5B32B4] focus:border-[#5B32B4] outline-none transition-all" name="buildingName" value={form.buildingName} onChange={handleChange} placeholder="Enter Building Name / Premise" />
                                </div>

                                {/* Area */}
                                <div>
                                    <label className="block text-xs font-bold text-slate-600 mb-2">Area *</label>
                                    <input type="text" className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium text-slate-700 focus:ring-2 focus:ring-[#5B32B4] focus:border-[#5B32B4] outline-none transition-all" name="area" value={form.area} onChange={handleChange} placeholder="Enter area" required />
                                </div>

                                {/* City */}
                                <div>
                                    <label className="block text-xs font-bold text-slate-600 mb-2">City *</label>
                                    <select className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium text-slate-700 focus:ring-2 focus:ring-[#5B32B4] focus:border-[#5B32B4] outline-none transition-all" name="city" value={form.city} onChange={handleChange} required>
                                        <option value="">Select City</option>
                                        {cityOptions.map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                </div>

                                {/* Nearby Address */}
                                <div className="md:col-span-2">
                                    <label className="block text-xs font-bold text-slate-600 mb-2">Nearby Address</label>
                                    <input type="text" className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium text-slate-700 focus:ring-2 focus:ring-[#5B32B4] focus:border-[#5B32B4] outline-none transition-all" name="nearbyAddress" value={form.nearbyAddress} onChange={handleChange} placeholder="Enter complete Nearby Address" />
                                </div>

                                {/* Occupancy Type with Toggle state */}
                                {propertyTypeSelection === 'Residential Rent' && (
                                    <div className="relative">
                                        <label className="block text-xs font-bold text-slate-600 mb-2">Occupancy Type</label>
                                        <div
                                            onClick={() => setOccupancyOpen(!occupancyOpen)}
                                            className={`w-full bg-white border rounded-xl px-4 py-3 text-sm font-medium text-slate-700 flex justify-between items-center cursor-pointer select-none transition-all ${occupancyOpen ? 'border-blue-400 ring-2 ring-blue-50' : 'border-slate-200'}`}
                                        >
                                            <span>{form.occupancyType}</span>
                                            <i className={`fa-solid fa-chevron-down text-slate-400 text-xs transition-transform duration-200 ${occupancyOpen ? 'rotate-180' : ''}`}></i>
                                        </div>

                                        {occupancyOpen && (
                                            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl overflow-hidden shadow-lg z-50">
                                                {['Only For Family', 'Only For Bachelors', 'Family-Bachelors'].map(opt => (
                                                    <div
                                                        key={opt}
                                                        onClick={() => {
                                                            setForm(p => ({ ...p, occupancyType: opt }))
                                                            setOccupancyOpen(false)
                                                        }}
                                                        className={`px-4 py-3 text-sm font-medium cursor-pointer transition-all ${form.occupancyType === opt ? 'bg-blue-600 text-white' : 'text-slate-700 hover:bg-slate-50'}`}
                                                    >
                                                        {opt}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Property Age */}
                                <div>
                                    <label className="block text-xs font-bold text-slate-600 mb-2">Property Age</label>
                                    <select className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium text-slate-700 focus:ring-2 focus:ring-[#5B32B4] focus:border-[#5B32B4] outline-none transition-all" name="propertyAge" value={form.propertyAge} onChange={handleChange}>
                                        <option value="">Select Age</option>
                                        <option value="Under Construction">Under Construction</option>
                                        <option value="New Construction">New Construction</option>
                                        <option value="1-5 Yrs">1-5 Yrs</option>
                                        <option value="5-10 Yrs">5-10 Yrs</option>
                                        <option value="10+ Yrs">10+ Yrs</option>
                                    </select>
                                </div>

                                {/* Floor */}
                                <div>
                                    <label className="block text-xs font-bold text-slate-600 mb-2">Floor</label>
                                    <input type="text" className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium text-slate-700 focus:ring-2 focus:ring-[#5B32B4] focus:border-[#5B32B4] outline-none transition-all" name="floor" value={form.floor} onChange={handleChange} placeholder="Enter Floor Number" />
                                </div>

                                {/* Washrooms */}
                                <div>
                                    <label className="block text-xs font-bold text-slate-600 mb-2">Washroom</label>
                                    <select className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium text-slate-700 focus:ring-2 focus:ring-[#5B32B4] focus:border-[#5B32B4] outline-none transition-all" name="washrooms" value={form.washrooms} onChange={handleChange}>
                                        <option value="1">1</option>
                                        <option value="2">2</option>
                                        <option value="3">3</option>
                                        <option value="4">4</option>
                                        <option value="5">5</option>
                                        <option value="5+">5+</option>
                                    </select>
                                </div>
                            </div>

                            {/* BHK Configuration */}
                            <div className="space-y-3">
                                <label className="block text-xs font-bold text-slate-600">BHK Configuration</label>
                                <div className="flex flex-wrap gap-2.5">
                                    {bhkOptions.map(opt => (
                                        <button
                                            type="button"
                                            key={opt}
                                            onClick={() => setForm(p => ({ ...p, bhkConfiguration: opt }))}
                                            className={`px-5 py-2 text-xs font-bold rounded-full border transition-all duration-200 select-none ${form.bhkConfiguration === opt ? 'bg-[#5B32B4]/10 text-[#5B32B4] border-[#5B32B4] shadow-sm' : 'bg-[#f1f5f9] text-slate-600 border-transparent hover:bg-slate-200'}`}
                                        >
                                            {opt}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Furnishing Type */}
                            <div className="space-y-3">
                                <label className="block text-xs font-bold text-slate-600">Furnishing Type</label>
                                <div className="flex flex-wrap gap-2.5">
                                    {furnishingOptions.map(opt => (
                                        <button
                                            type="button"
                                            key={opt}
                                            onClick={() => setForm(p => ({ ...p, furnishingType: opt }))}
                                            className={`px-5 py-2 text-xs font-bold rounded-full border transition-all duration-200 select-none ${form.furnishingType === opt ? 'bg-[#5B32B4]/10 text-[#5B32B4] border-[#5B32B4] shadow-sm' : 'bg-[#f1f5f9] text-slate-600 border-transparent hover:bg-slate-200'}`}
                                        >
                                            {opt}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Description */}
                            <div>
                                <label className="block text-xs font-bold text-slate-600 mb-2">Description</label>
                                <textarea
                                    className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium text-slate-700 focus:ring-2 focus:ring-[#5B32B4] focus:border-[#5B32B4] outline-none transition-all min-h-[140px]"
                                    name="description"
                                    value={form.description}
                                    onChange={handleChange}
                                    placeholder="Enter additional details..."
                                ></textarea>
                            </div>

                            {/* Step 2 Footer buttons */}
                            <div className="flex justify-between items-center pt-8 border-t border-gray-100">
                                <button
                                    type="button"
                                    onClick={prevStep}
                                    className="flex items-center gap-2 text-slate-600 hover:text-[#5B32B4] font-bold text-sm transition-colors active:scale-95"
                                >
                                    <i className="fa-solid fa-chevron-left text-xs"></i> Previous
                                </button>

                                <button
                                    type="submit"
                                    className="px-8 py-3 bg-[#5B32B4] hover:bg-slate-800 text-white rounded-xl font-bold text-sm transition-all shadow-md hover:shadow-lg active:scale-95 flex items-center justify-center gap-2 select-none"
                                >
                                    Next <i className="fa-solid fa-chevron-right text-xs"></i>
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                {/* Specific form for Builder + Residential */}
                {step === 2 && isBuilderResidential && (
                    <div className="space-y-8">
                        <h2 className="text-2xl font-bold text-[#0f172a] leading-none">Property Details</h2>

                        <form onSubmit={nextStep} className="space-y-8">
                            {/* Category and Sub Category Row */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Property Category */}
                                <div>
                                    <label className="block text-xs font-bold text-slate-600 mb-2">Property Category *</label>
                                    <select className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium text-slate-700 focus:ring-2 focus:ring-[#5B32B4] focus:border-[#5B32B4] outline-none transition-all" name="propertyCategory" value={form.propertyCategory} onChange={handleCategoryChange} required>
                                        <option value="RESIDENTIAL">RESIDENTIAL</option>
                                        <option value="COMMERCIAL">COMMERCIAL</option>
                                        <option value="PG / CO-LIVING">PG / CO-LIVING</option>
                                        <option value="PLOT">PLOT</option>
                                        <option value="LAND">LAND</option>
                                        <option value="BANK AUCTION PROPERTY">BANK AUCTION PROPERTY</option>
                                    </select>
                                </div>

                                {/* Sub Category */}
                                <div>
                                    <label className="block text-xs font-bold text-slate-600 mb-2">Sub Category *</label>
                                    <select className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium text-slate-700 focus:ring-2 focus:ring-[#5B32B4] focus:border-[#5B32B4] outline-none transition-all" name="subCategory" value={form.subCategory} onChange={handleChange} required>
                                        {(subCategoryMapping[form.propertyCategory] || []).map(sc => (
                                            <option key={sc} value={sc}>{sc}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Row 1: 3 columns */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {/* Price Min */}
                                <div>
                                    <label className="block text-xs font-bold text-slate-600 mb-2">Price Min *</label>
                                    <div className="relative">
                                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-sm">₹</span>
                                        <input type="number" className="w-full bg-white border border-slate-200 rounded-xl pl-8 pr-4 py-3 text-sm font-medium text-slate-700 focus:ring-2 focus:ring-[#5B32B4] focus:border-[#5B32B4] outline-none transition-all" name="priceMin" value={form.priceMin} onChange={handleChange} placeholder="Enter minimum price" required />
                                    </div>
                                </div>

                                {/* Price Max */}
                                <div>
                                    <label className="block text-xs font-bold text-slate-600 mb-2">Price Max *</label>
                                    <div className="relative">
                                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-sm">₹</span>
                                        <input type="number" className="w-full bg-white border border-slate-200 rounded-xl pl-8 pr-4 py-3 text-sm font-medium text-slate-700 focus:ring-2 focus:ring-[#5B32B4] focus:border-[#5B32B4] outline-none transition-all" name="priceMax" value={form.priceMax} onChange={handleChange} placeholder="Enter maximum price" required />
                                    </div>
                                </div>

                                {/* Size */}
                                <div>
                                    <label className="block text-xs font-bold text-slate-600 mb-2">Size</label>
                                    <div className="flex border border-slate-200 rounded-xl overflow-hidden bg-white focus-within:ring-2 focus-within:ring-[#5B32B4] focus-within:border-[#5B32B4] transition-all">
                                        <select className="bg-slate-50 border-r border-slate-200 px-3 py-3 text-sm font-medium text-slate-600 outline-none">
                                            <option>SQFT</option>
                                        </select>
                                        <input type="number" className="w-full px-4 py-3 text-sm font-medium text-slate-700 outline-none" name="size" value={form.size} onChange={handleChange} placeholder="Enter area in SQFT" />
                                    </div>
                                </div>
                            </div>

                            {/* Row 2: 3 columns */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {/* Building Name */}
                                <div>
                                    <label className="block text-xs font-bold text-slate-600 mb-2">Building Name / Premise Name</label>
                                    <input type="text" className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium text-slate-700 focus:ring-2 focus:ring-[#5B32B4] focus:border-[#5B32B4] outline-none transition-all" name="buildingName" value={form.buildingName} onChange={handleChange} placeholder="Enter Building Name / Premise Name" />
                                </div>

                                {/* Area */}
                                <div>
                                    <label className="block text-xs font-bold text-slate-600 mb-2">Area *</label>
                                    <input type="text" className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium text-slate-700 focus:ring-2 focus:ring-[#5B32B4] focus:border-[#5B32B4] outline-none transition-all" name="area" value={form.area} onChange={handleChange} placeholder="Enter area" required />
                                </div>

                                {/* City */}
                                <div>
                                    <label className="block text-xs font-bold text-slate-600 mb-2">City *</label>
                                    <select className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium text-slate-700 focus:ring-2 focus:ring-[#5B32B4] focus:border-[#5B32B4] outline-none transition-all" name="city" value={form.city} onChange={handleChange} required>
                                        <option value="">Select City</option>
                                        {cityOptions.map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                </div>
                            </div>

                            {/* Row 3: 1 full width column */}
                            <div>
                                <label className="block text-xs font-bold text-slate-600 mb-2">Nearby Address</label>
                                <input type="text" className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium text-slate-700 focus:ring-2 focus:ring-[#5B32B4] focus:border-[#5B32B4] outline-none transition-all" name="nearbyAddress" value={form.nearbyAddress} onChange={handleChange} placeholder="Enter complete Nearby Address" />
                            </div>

                            {/* Row 4: 3 columns */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {/* Property Age */}
                                <div>
                                    <label className="block text-xs font-bold text-slate-600 mb-2">Property Age</label>
                                    <select className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium text-slate-700 focus:ring-2 focus:ring-[#5B32B4] focus:border-[#5B32B4] outline-none transition-all" name="propertyAge" value={form.propertyAge} onChange={handleChange}>
                                        <option value="">Select Option</option>
                                        <option value="Under Construction">Under Construction</option>
                                        <option value="New Construction">New Construction</option>
                                        <option value="1-5 Yrs">1-5 Yrs</option>
                                        <option value="5-10 Yrs">5-10 Yrs</option>
                                        <option value="10+ Yrs">10+ Yrs</option>
                                    </select>
                                </div>

                                {/* Floor */}
                                <div>
                                    <label className="block text-xs font-bold text-slate-600 mb-2">Floor</label>
                                    <input type="text" className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium text-slate-700 focus:ring-2 focus:ring-[#5B32B4] focus:border-[#5B32B4] outline-none transition-all" name="floor" value={form.floor} onChange={handleChange} placeholder="Enter Floor Number" />
                                </div>

                                {/* Washrooms */}
                                <div>
                                    <label className="block text-xs font-bold text-slate-600 mb-2">Washroom</label>
                                    <select className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium text-slate-700 focus:ring-2 focus:ring-[#5B32B4] focus:border-[#5B32B4] outline-none transition-all" name="washrooms" value={form.washrooms} onChange={handleChange}>
                                        <option value="1">1</option>
                                        <option value="2">2</option>
                                        <option value="3">3</option>
                                        <option value="4">4</option>
                                        <option value="5">5</option>
                                        <option value="5+">5+</option>
                                    </select>
                                </div>
                            </div>

                            {/* Row 5: 3 columns */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {/* No. of Blocks */}
                                <div>
                                    <label className="block text-xs font-bold text-slate-600 mb-2">No. of Blocks</label>
                                    <input type="text" className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium text-slate-700 focus:ring-2 focus:ring-[#5B32B4] focus:border-[#5B32B4] outline-none transition-all" name="noOfBlocks" value={form.noOfBlocks} onChange={handleChange} placeholder="Enter Block Number" />
                                </div>

                                {/* Possession Date */}
                                <div>
                                    <label className="block text-xs font-bold text-slate-600 mb-2">Possession Date</label>
                                    <input type="date" className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium text-slate-700 focus:ring-2 focus:ring-[#5B32B4] focus:border-[#5B32B4] outline-none transition-all" name="possessionDate" value={form.possessionDate} onChange={handleChange} />
                                </div>

                                {/* Launch Date */}
                                <div>
                                    <label className="block text-xs font-bold text-slate-600 mb-2">Launch Date</label>
                                    <input type="date" className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium text-slate-700 focus:ring-2 focus:ring-[#5B32B4] focus:border-[#5B32B4] outline-none transition-all" name="launchDate" value={form.launchDate} onChange={handleChange} />
                                </div>
                            </div>

                            {/* Row 6: 3 columns */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {/* RERA Id */}
                                <div>
                                    <label className="block text-xs font-bold text-slate-600 mb-2">RERA Id</label>
                                    <input type="text" className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium text-slate-700 focus:ring-2 focus:ring-[#5B32B4] focus:border-[#5B32B4] outline-none transition-all" name="reraId" value={form.reraId} onChange={handleChange} placeholder="Enter RERA Id" />
                                </div>

                                {/* Total Unit */}
                                <div>
                                    <label className="block text-xs font-bold text-slate-600 mb-2">Total Unit</label>
                                    <input type="number" className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium text-slate-700 focus:ring-2 focus:ring-[#5B32B4] focus:border-[#5B32B4] outline-none transition-all" name="totalUnit" value={form.totalUnit} onChange={handleChange} placeholder="Enter total units" />
                                </div>

                                {/* Facing */}
                                <div>
                                    <label className="block text-xs font-bold text-slate-600 mb-2">FACING</label>
                                    <select className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium text-slate-700 focus:ring-2 focus:ring-[#5B32B4] focus:border-[#5B32B4] outline-none transition-all" name="facing" value={form.facing} onChange={handleChange}>
                                        <option value="">Select Facing</option>
                                        {facingOptions.map(f => <option key={f} value={f}>{f}</option>)}
                                    </select>
                                </div>
                            </div>

                            {/* Row 7: 3 columns */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {/* Type Of Parking */}
                                <div>
                                    <label className="block text-xs font-bold text-slate-600 mb-2">Type Of Parking</label>
                                    <select className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium text-slate-700 focus:ring-2 focus:ring-[#5B32B4] focus:border-[#5B32B4] outline-none transition-all" name="typeOfParking" value={form.typeOfParking} onChange={handleChange}>
                                        <option value="">Select Parking Type</option>
                                        {parkingOptions.map(p => <option key={p} value={p}>{p}</option>)}
                                    </select>
                                </div>

                                {/* No. of Parking */}
                                <div>
                                    <label className="block text-xs font-bold text-slate-600 mb-2">No. of Parking</label>
                                    <input type="number" className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium text-slate-700 focus:ring-2 focus:ring-[#5B32B4] focus:border-[#5B32B4] outline-none transition-all" name="noOfParking" value={form.noOfParking} onChange={handleChange} placeholder="Enter Number of Parking" />
                                </div>

                                {/* BASIC RATE */}
                                <div>
                                    <label className="block text-xs font-bold text-slate-600 mb-2">BASIC RATE</label>
                                    <input type="number" className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium text-slate-700 focus:ring-2 focus:ring-[#5B32B4] focus:border-[#5B32B4] outline-none transition-all" name="basicRate" value={form.basicRate} onChange={handleChange} placeholder="Enter Basic Rate" />
                                </div>
                            </div>

                            {/* Row 8: DP RATE in 3 columns grid */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-slate-600 mb-2">DP RATE</label>
                                    <input type="number" className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium text-slate-700 focus:ring-2 focus:ring-[#5B32B4] focus:border-[#5B32B4] outline-none transition-all" name="dpRate" value={form.dpRate} onChange={handleChange} placeholder="Enter DP Rate" />
                                </div>
                                <div></div>
                                <div></div>
                            </div>

                            {/* BHK Configuration */}
                            <div className="space-y-3">
                                <label className="block text-xs font-bold text-slate-600">BHK Configuration</label>
                                <div className="flex flex-wrap gap-2.5">
                                    {bhkOptions.map(opt => (
                                        <button
                                            type="button"
                                            key={opt}
                                            onClick={() => toggleBhkConfiguration(opt)}
                                            className={`px-4 py-2 text-xs font-medium rounded-full border transition-all duration-200 select-none ${(form.bhkConfigurations || []).includes(opt) ? 'bg-[#5B32B4]/10 text-[#5B32B4] border-[#5B32B4] shadow-sm font-bold' : 'bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-200'}`}
                                        >
                                            {opt}
                                        </button>
                                    ))}
                                </div>
                                <p className="text-xs text-slate-400 font-medium">You can select multiple BHK configurations for builder projects</p>
                            </div>

                            {/* Furnishing Type */}
                            <div className="space-y-3">
                                <label className="block text-xs font-bold text-slate-600">Furnishing Type</label>
                                <div className="flex flex-wrap gap-2.5">
                                    {furnishingOptions.map(opt => (
                                        <button
                                            type="button"
                                            key={opt}
                                            onClick={() => setForm(p => ({ ...p, furnishingType: opt }))}
                                            className={`px-4 py-2 text-xs font-medium rounded-full border transition-all duration-200 select-none ${form.furnishingType === opt ? 'bg-[#5B32B4]/10 text-[#5B32B4] border-[#5B32B4] shadow-sm font-bold' : 'bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-200'}`}
                                        >
                                            {opt}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Description */}
                            <div>
                                <label className="block text-xs font-bold text-slate-600 mb-2">Description</label>
                                <textarea
                                    className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium text-slate-700 focus:ring-2 focus:ring-[#5B32B4] focus:border-[#5B32B4] outline-none transition-all min-h-[140px]"
                                    name="description"
                                    value={form.description}
                                    onChange={handleChange}
                                    placeholder="Enter additional details..."
                                ></textarea>
                            </div>

                            {/* Step 2 Footer buttons */}
                            <div className="flex justify-between items-center pt-8 border-t border-gray-100">
                                <button
                                    type="button"
                                    onClick={prevStep}
                                    className="flex items-center gap-2 text-slate-600 hover:text-[#5B32B4] font-bold text-sm transition-colors active:scale-95"
                                >
                                    <i className="fa-solid fa-chevron-left text-xs"></i> Previous
                                </button>

                                <button
                                    type="submit"
                                    className="px-8 py-3 bg-[#5B32B4] hover:bg-slate-800 text-white rounded-xl font-bold text-sm transition-all shadow-md hover:shadow-lg active:scale-95 flex items-center justify-center gap-2 select-none"
                                >
                                    Next <i className="fa-solid fa-chevron-right text-xs"></i>
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                {step === 3 && (
                    <div className="space-y-8">
                        <h2 className="text-2xl font-bold text-[#0f172a] mb-6 leading-none">Property Amenities</h2>

                        <div className="p-6 bg-slate-50 border border-slate-200 rounded-2xl">
                            <h3 className="text-base font-bold text-slate-700 mb-4">Select Amenities (with icons)</h3>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 select-none">
                                {commonAmenities.map(am => (
                                    <div
                                        key={am}
                                        onClick={() => toggleAmenity(am)}
                                        className={`p-4 rounded-xl border-2 flex items-center gap-3 cursor-pointer transition-all duration-200 ${amenities.includes(am) ? 'bg-[#5B32B4]/10 border-[#5B32B4] text-[#5B32B4] shadow-sm' : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'}`}
                                    >
                                        <i className="fa-solid fa-circle-check"></i>
                                        <span className="text-xs font-bold">{am}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Step 3 Footer buttons */}
                        <div className="flex justify-between items-center pt-8 border-t border-gray-100">
                            <button
                                type="button"
                                onClick={prevStep}
                                className="flex items-center gap-2 text-slate-600 hover:text-[#5B32B4] font-bold text-sm transition-colors active:scale-95"
                            >
                                <i className="fa-solid fa-chevron-left text-xs"></i> Previous
                            </button>

                            <button
                                type="button"
                                onClick={nextStep}
                                className="px-8 py-3 bg-[#5B32B4] hover:bg-slate-800 text-white rounded-xl font-bold text-sm transition-all shadow-md hover:shadow-lg active:scale-95 flex items-center justify-center gap-2 select-none"
                            >
                                Next <i className="fa-solid fa-chevron-right text-xs"></i>
                            </button>
                        </div>
                    </div>
                )}

                {step === 4 && (
                    <div className="space-y-8">
                        <h2 className="text-2xl font-bold text-[#0f172a] mb-6 leading-none">Contact & Media</h2>

                        <div className="p-6 bg-slate-50 border border-slate-200 rounded-2xl flex items-center justify-center min-h-[150px]">
                            <p className="text-sm font-bold text-slate-600 text-center">Your property is now ready for publishing.</p>
                        </div>

                        {/* Step 4 Footer Buttons */}
                        <div className="flex justify-between items-center pt-8 border-t border-gray-100">
                            <button
                                type="button"
                                onClick={prevStep}
                                className="flex items-center gap-2 text-slate-600 hover:text-[#5B32B4] font-bold text-sm transition-colors active:scale-95"
                            >
                                <i className="fa-solid fa-chevron-left text-xs"></i> Previous
                            </button>

                            <button
                                type="button"
                                disabled={submitting}
                                onClick={handleSubmit}
                                className="px-8 py-3 bg-[#5B32B4] hover:bg-slate-800 text-white rounded-xl font-bold text-sm transition-all shadow-md hover:shadow-lg active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                {submitting ? (
                                    <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div> Publishing...</>
                                ) : (
                                    'Publish'
                                )}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </AgentLayout>
    )
}
